import React, { useEffect, useState } from "react";
import { makeStyles, Theme } from "@material-ui/core/styles";
import IconButton from "@material-ui/core/IconButton";
import ArrowBackIosIcon from "@material-ui/icons/ArrowBackIos";
import ArrowForwardIosIcon from "@material-ui/icons/ArrowForwardIos";
import { Caption, Body1 } from "../Typography";
import SingleSelectDropdown from "../Dropdown/SingleSelectDropdown";
import { colorsDark } from "../../themes";

interface IProps<T> {
  data: T[];
  onSegmentChange: (segment: T[]) => void;
  defaultRowsPerPage?: number;
  clampPageToData?: boolean;
  hideRowPerPageDropDown?: boolean;
  /** Render "Rows per page" as the dropdown's own floating label instead of as an adjacent caption. */
  rowsPerPageLabelInDropdown?: boolean;
  /** Override the rows-per-page dropdown's fixed width (defaults to 128px with a label, 80px without). */
  dropdownWidth?: number;
}

interface StyleProps {
  dropdownWidth?: number;
}

const useStyles = makeStyles<Theme, StyleProps>((theme) => ({
  secondaryFont: {
    color: colorsDark.font.secondary,
  },
  container: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    height: theme.spacing(7),
  },
  caption: {
    marginTop: "auto",
    marginBottom: "auto",
  },
  menuDropDownContainer: {
    marginLeft: theme.spacing(2),
    paddingRight: theme.spacing(3),
  },
  menuDropDown: {
    "& .MuiFilledInput-root": {
      width: ({ dropdownWidth }) => dropdownWidth ?? 80,
    },
    "& .MuiSelect-filled.MuiSelect-select": {
      paddingRight: 0,
      minWidth: 20,
    },
  },
  menuDropDownWithLabel: {
    "& .MuiFilledInput-root": {
      width: ({ dropdownWidth }) => dropdownWidth ?? 128,
    },
  },
  pageNumberAndArrows: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing(3),
  },
  pageNumber: {
    minWidth: 50,
    margin: "auto",
  },
  arrows: {
    display: "flex",
    gap: theme.spacing(2),
  },
  leftArrow: {
    marginRight: theme.spacing(-2),
    paddingLeft: 0,
    paddingRight: theme.spacing(1),
    "& .MuiSvgIcon-root": {
      marginLeft: theme.spacing(2),
    },
    height: theme.spacing(3),
    width: theme.spacing(3),
  },
  rightArrow: {
    height: theme.spacing(3),
    width: theme.spacing(3),
  },
}));

export default function ImprovedPagination<T>({
  data,
  onSegmentChange,
  defaultRowsPerPage,
  clampPageToData,
  hideRowPerPageDropDown,
  rowsPerPageLabelInDropdown,
  dropdownWidth,
}: IProps<T>) {
  const [rowsPerPage, setRowsPerPage] = useState<number>(defaultRowsPerPage ?? 10);
  const [pageNumber, setPageNumber] = useState<number>(0);

  const dataLength = data.length;
  const pageCount = Math.max(Math.ceil(dataLength / rowsPerPage), 1);
  const currentPage = clampPageToData ? Math.min(pageNumber, pageCount - 1) : pageNumber;
  const startIndex = Math.max(Math.min(rowsPerPage * currentPage, dataLength - 1), 0);
  const endIndex = Math.max(Math.min(startIndex + rowsPerPage, dataLength), 0);

  const classes = useStyles({ dropdownWidth });

  useEffect(() => {
    onSegmentChange(data.slice(startIndex, endIndex));
  }, [data, onSegmentChange, startIndex, endIndex]);

  useEffect(() => {
    if (clampPageToData && pageNumber !== currentPage) {
      setPageNumber(currentPage);
    }
  }, [clampPageToData, currentPage, pageNumber]);

  const rowsPerPageOptions = [
    { id: "5", label: "5" },
    { id: "10", label: "10" },
    { id: "25", label: "25" },
    { id: "50", label: "50" },
  ];

  if (dataLength < 1) {
    return null;
  }

  return (
    <div className={classes.container}>
      {!hideRowPerPageDropDown && (
        <>
          {!rowsPerPageLabelInDropdown && (
            <Caption className={`${classes.secondaryFont} ${classes.caption}`}>{"Rows per page"}</Caption>
          )}
          <div className={classes.menuDropDownContainer}>
            <SingleSelectDropdown
              label={rowsPerPageLabelInDropdown ? "Rows per page" : undefined}
              menuWidth={80}
              value={rowsPerPage.toString()}
              options={rowsPerPageOptions}
              onChange={(val) => {
                setRowsPerPage(Number(val));
              }}
              className={`${classes.menuDropDown}${
                rowsPerPageLabelInDropdown ? ` ${classes.menuDropDownWithLabel}` : ""
              }`}
            />
          </div>
        </>
      )}
      <div className={classes.pageNumberAndArrows}>
        <Body1 className={`${classes.secondaryFont} ${classes.pageNumber}`}>
          {startIndex + 1}-{endIndex}
          {` of ${dataLength}`}
        </Body1>
        <div className={classes.arrows}>
          <IconButton
            className={classes.leftArrow}
            aria-label="Previous page"
            onClick={() => setPageNumber(Math.max(0, currentPage - 1))}
            disabled={currentPage === 0}
          >
            <ArrowBackIosIcon />
          </IconButton>
          <IconButton
            className={classes.rightArrow}
            aria-label="Next page"
            onClick={() => setPageNumber(Math.min(pageCount - 1, currentPage + 1))}
            disabled={currentPage >= pageCount - 1}
          >
            <ArrowForwardIosIcon />
          </IconButton>
        </div>
      </div>
    </div>
  );
}
